import { FC, memo, useEffect, useMemo, useState } from 'react';

import { Ad } from '@adflow/ad';
import { ConsoleAPIGraph, CreativeHubTypes } from '@adflow/graphqlgen';
import { AdElement, AdflowAPI, SoccerStages, isTextElement, parseElementName } from '@adflow/types';
import { resolveElementVariables, buildBrandVariablesMap } from '@adflow/variable-resolvers';
import { svgUtils } from '@adflow/utils';
import { Box } from '@chakra-ui/react';

import type { SoccerFixture } from '../../soccerPreview';
import { useLoadFontsForAd } from '../../useFonts';
import PreviewOverlay from '../PreviewOverlay';
import SelectOverlay from '../SelectOverlay';
import { useMappedElements } from '../previewutils';
import { PREVIEW_MAX_SIZE, getPreviewScaledSize } from '../previewConstants';

type Props = {
  elements: Array<AdElement>;
  brand: ConsoleAPIGraph.Brand | null;
  brandKit?: CreativeHubTypes.Brand | null;
  fonts: Array<{
    fontFamily: string;
    fontFamilyId?: string;
    weight: number;
    style: string;
    stretch?: string;
    src: string;
  }>;
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions | null;
  selectedElementId?: string | null;
  isIndexPreview: boolean;
  forceTopLeftOrigin?: boolean;
  customizations?: Record<string, string> | null;
  svgIdPrefix?: string | null;
  dataSources?: Array<{ id: string; type: string }>;
  activeSoccerFixture?: SoccerFixture | null;
  resolveSoccerCustomAssetUrls?: boolean;
};

const PreviewComponent: FC<Props> = ({
  elements,
  brand,
  brandKit,
  fonts,
  displayOptions,
  selectedElementId,
  isIndexPreview,
  forceTopLeftOrigin,
  customizations,
  svgIdPrefix,
  dataSources,
  activeSoccerFixture,
  resolveSoccerCustomAssetUrls = true,
}) => {
  const [previewTick, setPreviewTick] = useState(0);

  useEffect(() => {
    if (activeSoccerFixture?.stage !== SoccerStages.PRE_EVENT) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setPreviewTick((currentTick) => currentTick + 1);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeSoccerFixture]);

  // Build brand variables map from brandKit if available
  const brandVariables = useMemo(() => {
    const variablesFromBrand = buildBrandVariablesMap(brandKit, fonts);
    if (!variablesFromBrand) return undefined;

    return variablesFromBrand;
  }, [brandKit, fonts]);

  // Ensure root background vectors have renderable SVG content before brand resolution
  const elementsWithBackground = useMemo(() => {
    return svgUtils.enrichRootBackgroundVectors(elements);
  }, [elements]);

  // Resolve brand variables in elements if brandKit is available
  const resolvedElements = useMemo(() => {
    if (!brandVariables) return elementsWithBackground;
    return resolveElementVariables(elementsWithBackground, brandVariables);
  }, [elementsWithBackground, brandVariables]);

  const customizedElements = useMemo(() => {
    let result = resolvedElements;

    // Apply customizations
    if (customizations && Object.keys(customizations).length > 0) {
      result = elements.map((el) => {
        const override = customizations[parseElementName(el.name).name];
        if (override !== undefined && isTextElement(el)) {
          return {
            ...el,
            data: { ...el.data, content: override },
          };
        }
        return el;
      });
    }

    // Prefix font families in text elements to match the prefixed fonts loaded by useFonts
    // This prevents brand fonts from affecting the rest of the page
    result = result.map((el) => {
      if (isTextElement(el) && el.data.style.fontFamily) {
        return {
          ...el,
          data: {
            ...el.data,
            style: {
              ...el.data.style,
              fontFamily: `adflow-preview-${el.data.style.fontFamily}`,
            },
          },
        };
      }
      return el;
    });

    return result;
  }, [resolvedElements, elements, customizations]);

  const mappedElements = useMappedElements(
    customizedElements,
    displayOptions,
    brand,
    dataSources,
    activeSoccerFixture,
    resolveSoccerCustomAssetUrls,
    previewTick,
  );

  const mappedElementsWithoutSvg = useMemo(
    () => mappedElements.filter((element) => element.type != 'SVG'),
    [mappedElements],
  );

  const svgElement = useMemo(() => elements.find((x) => x.type === 'SVG'), [elements]);

  const {
    width: boxWidth,
    height: boxHeight,
    scale: transformScale,
  } = useMemo(() => {
    if (svgElement?.size.width && svgElement?.size.height) {
      return getPreviewScaledSize(svgElement.size.width, svgElement.size.height);
    }
    return { width: PREVIEW_MAX_SIZE, height: PREVIEW_MAX_SIZE, scale: 1 };
  }, [svgElement]);

  const { width: contentWidth, height: contentHeight } = useMemo(() => {
    if (svgElement?.size.width && svgElement?.size.height) {
      return {
        width: svgElement.size.width,
        height: svgElement.size.height,
      };
    }

    return {
      width: boxWidth,
      height: boxHeight,
    };
  }, [boxHeight, boxWidth, svgElement]);

  let transformOrigin =
    isIndexPreview && svgElement?.size.width === 1200 && svgElement?.size.height === 1200
      ? 'top left'
      : isIndexPreview
      ? 'top'
      : 'top left';

  if (forceTopLeftOrigin) {
    transformOrigin = 'top left';
  }

  const scaledOffsetLeft = transformOrigin === 'top' ? -((contentWidth - boxWidth) / 2) : 0;

  useLoadFontsForAd(mappedElements, fonts);

  return (
    <Box
      position="relative"
      style={{
        width: `${boxWidth}px`,
        height: `${boxHeight}px`,
      }}
      data-testid="preview-frame"
    >
      <Box
        position="absolute"
        style={{
          top: '0px',
          left: `${scaledOffsetLeft}px`,
          width: `${contentWidth}px`,
          height: `${contentHeight}px`,
          transform: `scale(${transformScale})`,
          transformOrigin: transformOrigin,
        }}
        data-testid="preview-scaled-content"
      >
        <Ad adElements={mappedElements} isPreview={true} svgIdPrefix={svgIdPrefix} />
        {isIndexPreview ? (
          <></>
        ) : (
          <>
            <PreviewOverlay adElements={mappedElementsWithoutSvg} selectedId={selectedElementId} />
            <SelectOverlay adElements={mappedElementsWithoutSvg} />
          </>
        )}
      </Box>
    </Box>
  );
};

PreviewComponent.displayName = 'Preview';

// Memoize the component to prevent unnecessary re-renders
// Use a custom comparison function to efficiently compare props
const Preview = memo(PreviewComponent, (prevProps, nextProps) => {
  if (
    prevProps.isIndexPreview !== nextProps.isIndexPreview ||
    prevProps.forceTopLeftOrigin !== nextProps.forceTopLeftOrigin ||
    prevProps.selectedElementId !== nextProps.selectedElementId ||
    prevProps.svgIdPrefix !== nextProps.svgIdPrefix ||
    prevProps.resolveSoccerCustomAssetUrls !== nextProps.resolveSoccerCustomAssetUrls
  ) {
    return false;
  }

  if (prevProps.activeSoccerFixture !== nextProps.activeSoccerFixture) {
    return false;
  }

  if (prevProps.brand?._id !== nextProps.brand?._id) {
    return false;
  }

  if (prevProps.displayOptions !== nextProps.displayOptions) {
    return false;
  }

  if (prevProps.dataSources !== nextProps.dataSources) {
    return false;
  }

  // Check if fonts array changed
  if (prevProps.fonts !== nextProps.fonts) {
    if (
      prevProps.fonts.length !== nextProps.fonts.length ||
      prevProps.fonts.some((font, index) => font !== nextProps.fonts[index])
    ) {
      return false;
    }
  }

  // Check if elements array reference changed (indicates an element was updated)
  // Since the store creates a new array when updating elements, we need to check
  // if any element reference changed to detect updates
  if (prevProps.elements !== nextProps.elements) {
    // If array reference changed, check if length or any element reference changed
    if (
      prevProps.elements.length !== nextProps.elements.length ||
      prevProps.elements.some((el, index) => el !== nextProps.elements[index])
    ) {
      return false;
    }
  }

  // Compare customizations efficiently
  const prevCustomizations = prevProps.customizations || {};
  const nextCustomizations = nextProps.customizations || {};
  const prevKeys = Object.keys(prevCustomizations);
  const nextKeys = Object.keys(nextCustomizations);

  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    if (prevCustomizations[key] !== nextCustomizations[key]) {
      return false;
    }
  }

  // All comparisons passed - props are equal
  return true;
});

export default Preview;
export type { Props as PreviewProps };
